<script setup>
import { nextTick, ref, watch } from 'vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  confirmLabel: { type: String, default: 'Xác nhận' },
  cancelLabel: { type: String, default: 'Hủy' },
  busyLabel: { type: String, default: 'Đang xử lý…' },
  icon: { type: String, default: '↺' },
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' },
})
const emit = defineEmits(['confirm', 'cancel'])
const dialog = ref(null)
const cancelButton = ref(null)

watch(() => props.open, async isOpen => {
  await nextTick()
  if (!dialog.value) return
  if (isOpen && !dialog.value.open) {
    dialog.value.showModal()
    cancelButton.value?.focus()
  } else if (!isOpen && dialog.value.open) {
    dialog.value.close()
  }
}, { immediate: true })

function cancel() {
  if (!props.busy) emit('cancel')
}
</script>

<template>
  <Teleport to="body">
    <dialog ref="dialog" class="confirm-dialog" aria-labelledby="confirm-dialog-title" aria-describedby="confirm-dialog-content" @cancel.prevent="cancel">
      <div class="confirm-dialog__top">
        <span class="confirm-dialog__icon" aria-hidden="true">{{ icon }}</span>
        <button type="button" class="confirm-dialog__close" aria-label="Đóng" :disabled="busy" @click="cancel">×</button>
      </div>
      <h2 id="confirm-dialog-title">{{ title }}</h2>
      <div id="confirm-dialog-content" class="confirm-dialog__content"><slot /></div>
      <p v-if="error" class="confirm-dialog__error" role="alert">{{ error }}</p>
      <div class="confirm-dialog__actions">
        <button ref="cancelButton" type="button" class="btn btn-light" :disabled="busy" @click="cancel">{{ cancelLabel }}</button>
        <button type="button" class="btn btn-primary" :disabled="busy" @click="emit('confirm')">{{ busy ? busyLabel : confirmLabel }}</button>
      </div>
    </dialog>
  </Teleport>
</template>

<style scoped>
.confirm-dialog {
  width: min(460px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  margin: auto;
  padding: 28px;
  overflow: auto;
  border: 1px solid #e5e3f3;
  border-radius: 24px;
  color: #303958;
  background: #fff;
  box-shadow: 0 30px 90px rgba(24, 25, 64, .22);
}
.confirm-dialog::backdrop { background: rgba(24, 27, 55, .48); backdrop-filter: blur(3px); }
.confirm-dialog__top { display: flex; align-items: flex-start; justify-content: space-between; }
.confirm-dialog__icon { display: grid; place-items: center; width: 48px; height: 48px; border-radius: 15px; color: #5b51c7; background: #f0eeff; font-size: 29px; line-height: 1; }
.confirm-dialog__close { width: 30px; height: 30px; border: 0; border-radius: 8px; background: transparent; color: #8990a9; font-size: 25px; line-height: 1; cursor: pointer; }
.confirm-dialog__close:hover { background: #f5f4fb; color: #46406c; }
.confirm-dialog h2 { margin: 18px 0 10px; font-size: 22px; line-height: 1.3; letter-spacing: -.5px; }
.confirm-dialog__content { color: #68718d; font-size: 13px; line-height: 1.6; }
.confirm-dialog__content :deep(p) { margin: 12px 0 0; }
.confirm-dialog__error { margin: 16px 0 0; padding: 11px 13px; border-radius: 10px; color: #9d3535; background: #fff0ef; font-size: 12px; }
.confirm-dialog__actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
.confirm-dialog__actions .btn { min-width: 112px; }
@media (max-width: 480px) {
  .confirm-dialog { padding: 22px; }
  .confirm-dialog__actions { flex-direction: column-reverse; }
  .confirm-dialog__actions .btn { width: 100%; }
}
@media (max-height: 520px) {
  .confirm-dialog { max-height: calc(100vh - 20px); padding: 16px 22px; }
  .confirm-dialog__icon { width: 36px; height: 36px; border-radius: 11px; font-size: 23px; }
  .confirm-dialog h2 { margin: 8px 0 3px; font-size: 19px; }
  .confirm-dialog__content { font-size: 12px; line-height: 1.45; }
  .confirm-dialog__content :deep(p) { margin-top: 8px; }
  .confirm-dialog__actions { margin-top: 12px; }
  .confirm-dialog__actions .btn { min-height: 38px; }
}
</style>
